// TennisTrack Hub — Libreria Template | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Template.css';

const LibreriaTemplate = () => {
  const { utente } = useAuth();
  const navigate = useNavigate();
  const [sezione, setSezione] = useState('miei'); // 'miei' | 'esplora'
  const [templateMiei, setTemplateMiei] = useState([]);
  const [templateServer, setTemplateServer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  // Filtri esplora
  const [filtri, setFiltri] = useState({ tipo: '', livello: '', piano: '', cerca: '' });

  const caricaMiei = useCallback(async () => {
    try {
      const res = await api.get('/template/miei');
      setTemplateMiei(res.data.template);
    } catch { setErrore('Errore caricamento template.'); }
  }, []);

  const caricaServer = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtri.tipo)   params.tipo = filtri.tipo;
      if (filtri.livello) params.livello = filtri.livello;
      if (filtri.piano)  params.piano = filtri.piano;
      if (filtri.cerca)  params.cerca = filtri.cerca;
      const res = await api.get('/template/server', { params });
      setTemplateServer(res.data.template);
    } catch { setErrore('Errore caricamento libreria.'); }
    finally { setLoading(false); }
  }, [filtri]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await caricaMiei();
      setLoading(false);
    };
    init();
  }, [caricaMiei]);

  useEffect(() => {
    if (sezione === 'esplora') caricaServer();
  }, [sezione, caricaServer]);

  const forkTemplate = async (templateId) => {
    try {
      await api.post(`/template/fork/${templateId}`);
      await caricaMiei();
      setSezione('miei');
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
    }
  };

  const eliminaMio = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await api.delete(`/template/miei/${id}`);
      await caricaMiei();
    } catch { setErrore('Errore eliminazione.'); }
  };

  const pianoBadge = (piano) => ({
    demo: <span className="t-badge t-badge-demo">DEMO</span>,
    free: <span className="t-badge t-badge-free">FREE</span>,
    premium: <span className="t-badge t-badge-pro">⭐ PREMIUM</span>
  }[piano]);

  const limiteRaggiunto = utente?.piano === 'free' && templateMiei.length >= 4;

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>📋 Template</h1>
            <p>Gestisci i tuoi template o esplora la libreria</p>
          </div>
          {sezione === 'miei' && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/template/nuovo')}
              disabled={limiteRaggiunto}
              title={limiteRaggiunto ? 'Limite Free raggiunto (max 4)' : ''}
            >
              + Nuovo template
            </button>
          )}
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}

        {limiteRaggiunto && (
          <div className="alert-upgrade">
            <span>⚠️ Hai raggiunto il limite di 4 template del piano Free.</span>
            <span className="upgrade-link">Passa a Pro per template illimitati →</span>
          </div>
        )}

        {/* Tab sezioni */}
        <div className="tab-bar">
          <button className={`tab-btn ${sezione === 'miei' ? 'active' : ''}`} onClick={() => setSezione('miei')}>
            🗂 I miei template ({templateMiei.length})
          </button>
          <button className={`tab-btn ${sezione === 'esplora' ? 'active' : ''}`} onClick={() => setSezione('esplora')}>
            🌐 Esplora libreria
          </button>
        </div>

        {/* ── SEZIONE MIEI ── */}
        {sezione === 'miei' && (
          loading ? <div className="loading-msg">Caricamento...</div> :
          templateMiei.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>Nessun template salvato</h3>
              <p>Crea il tuo primo template o scaricane uno dalla libreria.</p>
              <div style={{ display:'flex', gap:'10px', justifyContent:'center', marginTop:'8px' }}>
                <button className="btn btn-primary" onClick={() => navigate('/template/nuovo')}>Crea template</button>
                <button className="btn btn-secondary" onClick={() => setSezione('esplora')}>Esplora libreria</button>
              </div>
            </div>
          ) : (
            <div className="template-grid">
              {templateMiei.map(t => (
                <div key={t.id} className="card template-card">
                  <div className="tc-header">
                    <span className="tc-tipo">{t.tipo === 'tennis' ? '🎾' : '💪'}</span>
                    {t.is_fork && <span className="tc-fork-badge">fork</span>}
                    {t.inviato_revisione && <span className="tc-revisione-badge">📤 in revisione</span>}
                  </div>
                  <h3 className="tc-titolo">{t.titolo}</h3>
                  {t.descrizione && <p className="tc-desc">{t.descrizione}</p>}
                  <div className="tc-meta">
                    <span className="livello-badge livello-{t.livello === 'principiante' ? 'verde' : t.livello === 'intermedio' ? 'giallo' : 'rosso'}">{t.livello}</span>
                  </div>
                  <div className="tc-actions">
                    <Link to={`/template/miei/${t.id}`} className="btn btn-secondary btn-sm">Apri</Link>
                    <Link to={`/template/miei/${t.id}/modifica`} className="btn btn-secondary btn-sm">✏️</Link>
                    <button className="btn-text-danger" onClick={() => eliminaMio(t.id)}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── SEZIONE ESPLORA ── */}
        {sezione === 'esplora' && (
          <>
            {/* Filtri */}
            <div className="template-filtri">
              <input
                type="text"
                placeholder="🔍 Cerca template..."
                value={filtri.cerca}
                onChange={e => setFiltri({ ...filtri, cerca: e.target.value })}
                className="filtro-search"
              />
              <select value={filtri.tipo} onChange={e => setFiltri({ ...filtri, tipo: e.target.value })}>
                <option value="">Tutti i tipi</option>
                <option value="tennis">🎾 Tennis</option>
                <option value="fitness">💪 Fitness</option>
              </select>
              <select value={filtri.livello} onChange={e => setFiltri({ ...filtri, livello: e.target.value })}>
                <option value="">Tutti i livelli</option>
                <option value="principiante">Principiante</option>
                <option value="intermedio">Intermedio</option>
                <option value="avanzato">Avanzato</option>
              </select>
              <select value={filtri.piano} onChange={e => setFiltri({ ...filtri, piano: e.target.value })}>
                <option value="">Tutti i piani</option>
                <option value="demo">Demo</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
              </select>
            </div>

            {loading ? <div className="loading-msg">Caricamento libreria...</div> :
             templateServer.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🌐</div>
                <h3>Nessun template trovato</h3>
                <p>Prova a modificare i filtri di ricerca.</p>
              </div>
            ) : (
              <div className="template-grid">
                {templateServer.map(t => (
                  <div key={t.id} className={`card template-card ${t.bloccato ? 'tc-bloccato' : ''}`}>
                    <div className="tc-header">
                      <span className="tc-tipo">{t.tipo === 'tennis' ? '🎾' : '💪'}</span>
                      {pianoBadge(t.piano)}
                    </div>
                    <h3 className="tc-titolo">{t.titolo}</h3>
                    {t.descrizione && <p className="tc-desc">{t.descrizione}</p>}
                    <div className="tc-meta">
                      <span className="tc-autore">di {t.autore_nome || '029 Project'}</span>
                      <span className="tc-utilizzi">📥 {t.n_utilizzi}</span>
                      {t.n_commenti > 0 && <span className="tc-commenti">💬 {t.n_commenti}</span>}
                    </div>
                    <div className="tc-actions">
                      {t.bloccato ? (
                        <span className="tc-lock">🔒 Pro</span>
                      ) : (
                        <>
                          <Link to={`/template/server/${t.id}`} className="btn btn-secondary btn-sm">Dettaglio</Link>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => forkTemplate(t.id)}
                            disabled={limiteRaggiunto}
                          >
                            💾 Salva
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </main>
    </div>
  );
};

export default LibreriaTemplate;
